import React, { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import api from '../../../api/api';
import useAdminDraftGuard from '../useAdminDraftGuard';
import DirectCatalogAlbumPicker from './DirectCatalogAlbumPicker';
import CanonicalAlbumMerge from './CanonicalAlbumMerge';
import CanonicalCandidateList from './CanonicalCandidateList';

export default function DirectCatalogAppleManagement() {
  const [albumId, setAlbumId] = useState('');
  const [appleMusicId, setAppleMusicId] = useState('');
  const [review, setReview] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [manualAlbum, setManualAlbum] = useState(null);
  const draft = useAdminDraftGuard({ albumId, appleMusicId }, { busy });
  const edit = (setter, value) => {
    setter(value);
    setReview(null);
    setConfirmed(false);
    setSuccess(false);
    setError('');
  };
  const request = async (apply) => {
    if (busy || !/^[1-9][0-9]*$/.test(albumId) || !/^[0-9]{1,30}$/.test(appleMusicId)) return;
    if (
      apply &&
      (!confirmed ||
        !review ||
        review.blockers.length ||
        !window.confirm(
          '동일한 발매판·녹음임을 확인했나요? 기존 기록은 유지하고 공식 정보를 연결합니다.',
        ))
    )
      return;
    setBusy(true);
    setError('');
    setSuccess(false);
    try {
      const response = await api.postAuth(
        `/admin/direct-catalog/albums/${albumId}/apple/${apply ? 'apply' : 'review'}`,
        {
          appleMusicId,
          ...(apply ? { fingerprint: review.fingerprint, confirmedSameEdition: true } : {}),
        },
      );
      setReview(apply ? null : response.data.data);
      setConfirmed(false);
      setSuccess(apply);
      if (apply) draft.markClean();
    } catch (e) {
      setError(e.message || '처리하지 못했습니다. 다시 비교해주세요.');
      setReview(null);
      setConfirmed(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Stack spacing={2}>
      <Typography variant="h6">직접 등록 앨범의 Apple Music 공식 정보 연결</Typography>
      <CanonicalCandidateList
        disabled={busy}
        onSelect={(manual, official) => {
          setManualAlbum(manual);
          edit(setAlbumId, String(manual.albumId));
          setAppleMusicId(official.appleMusicId);
        }}
      />
      <Alert severity="info">
        아래에서 앨범을 검색·선택하면 ID가 자동 입력됩니다. 수집 앨범의 중복 후보도 조회할 수 있지만
        자동 연결하지 않습니다. 앨범·트랙 내부 ID와 리뷰·별점·저장·플리를 유지합니다. 다른 판본,
        트랙 수·순서 차이, 5초 초과 러닝타임 차이, 이미 별도로 등록된 공식 앨범은 적용을 차단합니다.
        자동 병합이나 트랙 삭제는 하지 않습니다.
      </Alert>
      <DirectCatalogAlbumPicker
        source="POSTPOP"
        disabled={busy}
        onSelect={(item) => {
          setManualAlbum(item);
          edit(setAlbumId, String(item.albumId));
          setAppleMusicId(item.appleMusicId || '');
        }}
      />
      {manualAlbum && (
        <Typography>
          선택한 수동 앨범: {manualAlbum.title} · {manualAlbum.artistName}
        </Typography>
      )}
      <DirectCatalogAlbumPicker
        key={albumId}
        source="APPLE_MUSIC"
        manualAlbum={manualAlbum}
        disabled={busy}
        onSelect={(item) => edit(setAppleMusicId, item.appleMusicId)}
      />
      <TextField
        label="포스트팝 내부 앨범 ID"
        value={albumId}
        disabled={busy}
        onChange={(e) => {
          setManualAlbum(null);
          setAppleMusicId('');
          edit(setAlbumId, e.target.value);
        }}
      />
      <TextField
        label="Apple Music 앨범 ID"
        value={appleMusicId}
        disabled={busy}
        onChange={(e) => edit(setAppleMusicId, e.target.value)}
        helperText="공식 Apple Music 앨범 주소 끝의 숫자 ID"
      />
      <Button
        disabled={busy || !/^[1-9][0-9]*$/.test(albumId) || !/^[0-9]{1,30}$/.test(appleMusicId)}
        onClick={() => request(false)}
      >
        공식 정보 비교
      </Button>
      {error && <Alert severity="error">{error}</Alert>}
      {success && (
        <Alert severity="success">
          공식 정보와 전체 듣기·제공되는 미리듣기를 연결했습니다. 기존 내부 ID와 사용자 기록은
          유지됩니다.
        </Alert>
      )}
      {review && (
        <>
          <Typography>
            {review.originalTitle} → {review.officialTitle}
          </Typography>
          <Typography>
            트랙 수: {review.originalTrackCount} → {review.officialTrackCount}
          </Typography>
          {review.mergeImpact && (
            <Stack spacing={1}>
              <CanonicalAlbumMerge
                key={`${albumId}-${review.fingerprint}`}
                sourceAlbumId={Number(albumId)}
                impact={review.mergeImpact}
              />
              <Alert severity="warning">
                공식 앨범 #{review.mergeImpact.targetAlbumId}에 편입할 기록의 사전 점검입니다. 아직
                병합을 실행하지 않습니다. 아래 곡은 DB 기준 후보이며, 같은 녹음인지 확인해야 합니다.
              </Alert>
              <Table size="small" aria-label="병합 기록 사전 점검">
                <TableHead>
                  <TableRow>
                    <TableCell>기록</TableCell>
                    <TableCell>수동 앨범</TableCell>
                    <TableCell>공식 앨범</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    ['evaluations', '평가 전체(본문 리뷰 포함)'],
                    ['writtenReviews', '본문이 있는 리뷰'],
                    ['albumBookmarks', '앨범 저장'],
                    ['albumPlaylistEntries', '앨범 플리 항목'],
                    ['listeningRecords', '청취 기록'],
                    ['trackRatings', '곡 평가'],
                    ['trackPlaylistEntries', '곡 플리 항목'],
                  ].map(([key, label]) => (
                    <TableRow key={key}>
                      <TableCell>{label}</TableCell>
                      <TableCell>{review.mergeImpact.sourceRecords[key]}</TableCell>
                      <TableCell>{review.mergeImpact.targetRecords[key]}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography>
                양쪽 평가 사용자 {review.mergeImpact.conflictingEvaluationUsers}명 · 중복 저장{' '}
                {review.mergeImpact.overlappingAlbumBookmarks}건 · 중복 앨범 플리{' '}
                {review.mergeImpact.overlappingAlbumPlaylists}건 · 중복 청취 기간{' '}
                {review.mergeImpact.overlappingListeningPeriods}건
              </Typography>
              {review.mergeImpact.tracks.map((track) => (
                <Typography key={track.sourceTrackId}>
                  {track.title} (#{track.sourceTrackId}) →{' '}
                  {track.candidateTrackIds.length
                    ? track.candidateTrackIds.map((id) => `#${id}`).join(', ')
                    : '대응 후보 없음'}
                  {' · '}곡 평가 {track.ratingCount}건 · 곡 플리 {track.playlistEntryCount}건 · 양쪽
                  평가 사용자 {track.conflictingRatingUsers}명
                </Typography>
              ))}
            </Stack>
          )}
          {review.blockers.map((message) => (
            <Alert severity="error" key={message}>
              {message}
            </Alert>
          ))}
          {review.warnings.map((message) => (
            <Alert severity="warning" key={message}>
              {message}
            </Alert>
          ))}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>기존 곡 / ID</TableCell>
                <TableCell>시간(ms) 기존 → 공식</TableCell>
                <TableCell>미리듣기</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {review.tracks.map((track) => (
                <TableRow key={track.internalTrackId}>
                  <TableCell>
                    {track.title} / {track.internalTrackId}
                  </TableCell>
                  <TableCell>
                    {track.oldDurationMs} → {track.newDurationMs ?? '없음'}
                  </TableCell>
                  <TableCell>{track.previewAvailable ? '제공' : '없음'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <FormControlLabel
            control={
              <Checkbox
                disabled={busy || Boolean(review.blockers.length)}
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
            }
            label="동일한 발매판과 녹음임을 원본과 대조했고, 차이와 보존 항목을 확인했습니다."
          />
          <Button
            variant="contained"
            disabled={busy || !confirmed || Boolean(review.blockers.length)}
            onClick={() => request(true)}
          >
            확인한 공식 정보 연결
          </Button>
        </>
      )}
    </Stack>
  );
}
